import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Database, Play, Trash2, Clock, AlertTriangle, CheckCircle2, Loader2, Archive,
  Download, Upload, Lock, Unlock,
} from 'lucide-react';
import { sicherungen as api } from '../api';
import type { Sicherung, SicherungsPlan } from '../api';

/**
 * Die Datensicherungen einer Instanz.
 *
 * ══ WAS HIER GESICHERT WIRD, UND WAS NICHT ═════════════════════════════════
 *
 * Der Inhalt der Datenbank, als Dump. Nicht die Mediendateien, nicht die
 * Uploads — die liegen in Volumes, sind um Größenordnungen größer und haben
 * einen anderen Rhythmus. Das steht auch so auf der Karte: wer sie braucht,
 * soll nicht glauben, er habe sie hier schon.
 *
 * ══ WARUM DER PLAN ZWEI EBENEN HAT ═════════════════════════════════════════
 *
 * Ein Plan am Kunden gilt für alle seine Instanzen; einer an der Instanz
 * schlägt ihn. Die Karte sagt jeweils, welcher gerade greift — sonst stellt
 * jemand einen Wert ein und sieht nicht, dass er ihn gerade vom Kunden geerbt
 * und damit für alle anderen mitgeändert hätte.
 */

/** Minuten in etwas Lesbares — und zurück. */
const EINHEITEN = [
  { name: 'Minuten', faktor: 1 },
  { name: 'Stunden', faktor: 60 },
  { name: 'Tage', faktor: 1440 },
  { name: 'Wochen', faktor: 10080 },
] as const;

/** Einheiten fuer das Hoechstalter. Monate als 30 Tage — genau genug dafuer. */
const ALTER_EINHEITEN = [
  { name: 'Tagen', faktor: 1 },
  { name: 'Wochen', faktor: 7 },
  { name: 'Monaten', faktor: 30 },
] as const;

/** Tage in die groesste glatte Einheit zerlegen. */
function zerlegeTage(tage: number): { zahl: number; faktor: number } {
  for (const e of [...ALTER_EINHEITEN].reverse()) {
    if (tage % e.faktor === 0 && tage >= e.faktor) return { zahl: tage / e.faktor, faktor: e.faktor };
  }
  return { zahl: tage, faktor: 1 };
}

/** Die größte Einheit, in der die Zahl glatt aufgeht. */
function zerlege(minuten: number): { zahl: number; faktor: number } {
  for (const e of [...EINHEITEN].reverse()) {
    if (minuten % e.faktor === 0 && minuten >= e.faktor) {
      return { zahl: minuten / e.faktor, faktor: e.faktor };
    }
  }
  return { zahl: minuten, faktor: 1 };
}

function groesse(bytes: number | null): string {
  if (bytes === null) return '—';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}

function zeitpunkt(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

const AUSLOESER_TEXT: Record<Sicherung['ausloeser'], string> = {
  MANUELL: 'von Hand',
  PLAN: 'planmäßig',
  VOR_UPDATE: 'vor Update',
};

function StatusZeichen({ s }: { s: Sicherung }) {
  if (s.status === 'LAEUFT') {
    return <Loader2 size={13} className="shrink-0 animate-spin text-slate-400" aria-label="läuft" />;
  }
  if (s.status === 'OK') {
    return <CheckCircle2 size={13} className="shrink-0 text-emerald-600" aria-label="in Ordnung" />;
  }
  if (s.status === 'WEGGERAEUMT') {
    // Kein Fehler, sondern das Ende eines Lebenszyklus: der Datensatz bleibt,
    // damit die Historie keine Löcher hat, die Datei ist fort.
    return <Archive size={13} className="shrink-0 text-slate-400" aria-label="weggeräumt" />;
  }
  return <AlertTriangle size={13} className="shrink-0 text-red-600" aria-label="fehlgeschlagen" />;
}

/**
 * Zeitplan und Aufbewahrung — zwei Dinge, zwei Abschnitte.
 *
 * ══ WARUM DIE TRENNUNG SICHTBAR SEIN MUSS ══════════════════════════════════
 *
 * Vorher standen „Alle 1 Tage" und „stündlich / täglich / wöchentlich /
 * monatlich" untereinander und sahen aus wie eine Einheit — als wäre das
 * zweite eine Verfeinerung des ersten. Es sind aber zwei unabhängige Fragen:
 * wie oft gesichert wird, und wie lange das Ergebnis bleibt.
 *
 * Die Aufbewahrung sagt jetzt in ganzen Sätzen, was sie tut, statt in vier
 * Zahlen, deren Verfahren man kennen muss.
 */
function PlanFormular({
  plan,
  instanceId,
  onFertig,
}: {
  plan: SicherungsPlan | null;
  instanceId: string;
  onFertig: () => void;
}) {
  const anfang = zerlege(plan?.intervallMinuten ?? 1440);
  const [zahl, setZahl] = useState(String(anfang.zahl));
  const [faktor, setFaktor] = useState(anfang.faktor);
  const [aktiv, setAktiv] = useState(plan?.aktiv ?? true);

  const [anzahl, setAnzahl] = useState(String(plan?.behalteAnzahl ?? 10));
  // Die Tage werden in der groessten glatten Einheit gezeigt: 30 Tage sind
  // „1 Monat", 14 „2 Wochen". Wer 45 eintraegt, sieht 45 Tage.
  const altAnfang = zerlegeTage(plan?.loeschenNachTagen ?? 30);
  const [alter, setAlter] = useState(plan?.loeschenNachTagen ? String(altAnfang.zahl) : '');
  const [alterFaktor, setAlterFaktor] = useState(altAnfang.faktor);
  const [loeschschutz, setLoeschschutz] = useState(plan?.loeschschutz ?? false);
  const [maxMb, setMaxMb] = useState(plan?.maxBelegungMb ? String(plan.maxBelegungMb) : '');
  const [fehler, setFehler] = useState('');

  const speichern = useMutation({
    mutationFn: () =>
      api.planSetzen(instanceId, {
        aktiv,
        intervallMinuten: Math.max(5, Number(zahl) * faktor),
        behalteAnzahl: Math.max(1, Number(anzahl) || 1),
        loeschenNachTagen: alter.trim() ? Math.max(1, Number(alter) * alterFaktor) : null,
        loeschschutz,
        maxBelegungMb: maxMb.trim() ? Number(maxMb) : null,
      }),
    onSuccess: onFertig,
    onError: (e: unknown) =>
      setFehler(
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message
          ?? 'Der Plan liess sich nicht speichern.',
      ),
  });

  return (
    <div className="mt-3 space-y-5 rounded-lg border border-slate-200 bg-slate-50/60 p-4">
      {/* ── Wie oft ─────────────────────────────────────────────────────── */}
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          Wie oft gesichert wird
        </p>
        <label className="mt-2 flex items-center gap-2 text-xs font-medium text-slate-700">
          <input type="checkbox" checked={aktiv} onChange={(e) => setAktiv(e.target.checked)} />
          Regelmaessig sichern
        </label>
        {aktiv && (
          <div className="mt-2 flex items-center gap-1.5">
            <span className="text-xs text-slate-600">Alle</span>
            <input
              type="number" min={1} value={zahl} onChange={(e) => setZahl(e.target.value)}
              className="w-20 rounded-lg border border-slate-300 px-2 py-1.5 text-sm tabular-nums"
            />
            <select
              value={faktor} onChange={(e) => setFaktor(Number(e.target.value))}
              className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
            >
              {EINHEITEN.map((e) => (
                <option key={e.faktor} value={e.faktor}>{e.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* ── Wie lange ───────────────────────────────────────────────────── */}
      <div className="border-t border-slate-200 pt-4">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          Wie lange sie bleiben
        </p>

        <label className="mt-2 flex items-center gap-2 text-xs text-slate-700">
          <input
            type="checkbox" checked={loeschschutz}
            onChange={(e) => setLoeschschutz(e.target.checked)}
          />
          Nichts automatisch loeschen
        </label>

        {loeschschutz ? (
          <p className="mt-2 text-[11px] leading-snug text-amber-700">
            Jede Sicherung bleibt, bis ein Administrator sie ausdruecklich entfernt.
            Der Plattenplatz waechst dann mit jeder Sicherung.
          </p>
        ) : (
          <>
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <input
                type="number" min={1} value={anzahl} onChange={(e) => setAnzahl(e.target.value)}
                className="w-20 rounded-lg border border-slate-300 px-2 py-1.5 text-sm tabular-nums"
              />
              <span className="text-xs text-slate-600">Sicherungen aufheben</span>
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-slate-600">und nach</span>
              <input
                type="number" min={1} value={alter} placeholder="—"
                onChange={(e) => setAlter(e.target.value)}
                className="w-20 rounded-lg border border-slate-300 px-2 py-1.5 text-sm tabular-nums"
              />
              <select
                value={alterFaktor} onChange={(e) => setAlterFaktor(Number(e.target.value))}
                className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
              >
                {ALTER_EINHEITEN.map((e) => (
                  <option key={e.faktor} value={e.faktor}>{e.name}</option>
                ))}
              </select>
              <span className="text-xs text-slate-600">loeschen</span>
            </div>
            <p className="mt-1 text-[11px] leading-snug text-slate-500">
              Beides gilt zusammen: eine Sicherung bleibt, solange sie unter der Anzahl
              liegt <em>und</em> jung genug ist. Das Alter leer lassen heisst: nur die
              Anzahl zaehlt.
            </p>

            <label className="mt-3 flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-slate-600">Hoechstens</span>
              <input
                type="number" min={0} value={maxMb} placeholder="ohne Grenze"
                onChange={(e) => setMaxMb(e.target.value)}
                className="w-28 rounded-lg border border-slate-300 px-2 py-1.5 text-sm tabular-nums"
              />
              <span className="text-xs text-slate-600">MB belegen</span>
            </label>
          </>
        )}
      </div>

      {fehler && <p className="text-[11px] text-red-600">{fehler}</p>}

      <div className="flex items-center gap-2 border-t border-slate-200 pt-4">
        <button
          onClick={() => speichern.mutate()}
          disabled={speichern.isPending}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white disabled:opacity-50"
        >
          {speichern.isPending ? 'Speichert…' : 'Plan speichern'}
        </button>
        <button onClick={onFertig} className="text-xs text-slate-500 hover:text-slate-700">
          Abbrechen
        </button>
      </div>
    </div>
  );
}

/**
 * Die Rueckfrage vor dem Einspielen.
 *
 * Verlangt den Namen der Instanz. Ein „Wirklich?"-Dialog wird weggeklickt,
 * bevor er gelesen ist — einen Namen abzutippen zwingt dazu hinzusehen,
 * welche Instanz gerade ueberschrieben wird.
 */
function EinspielenDialog({
  sicherung, instanceId, stackName, onSchliessen,
}: {
  sicherung: Sicherung;
  instanceId: string;
  stackName: string;
  onSchliessen: () => void;
}) {
  const [eingabe, setEingabe] = useState('');
  const [ergebnis, setErgebnis] = useState<string | null>(null);
  const qc = useQueryClient();

  const einspielen = useMutation({
    mutationFn: () => api.einspielen(instanceId, sicherung.id, eingabe.trim()),
    onSuccess: (r) => {
      setErgebnis(r.meldung);
      qc.invalidateQueries({ queryKey: ['sicherungen', instanceId] });
    },
    onError: (e: unknown) =>
      setErgebnis(
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message
          ?? 'Das Einspielen ist fehlgeschlagen.',
      ),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
        <div className="flex items-start gap-2">
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-600" />
          <div>
            <h3 className="text-base font-semibold text-slate-800">Sicherung einspielen</h3>
            <p className="mt-1 text-xs leading-snug text-slate-600">
              Der Stand vom <strong>{zeitpunkt(sicherung.begonnenAm)}</strong> ersetzt den
              Inhalt von <code className="font-mono">{sicherung.dienst}</code>. Alles, was
              seitdem entstanden ist, ist danach fort.
            </p>
            <p className="mt-2 text-xs leading-snug text-slate-600">
              Vorher wird der jetzige Stand automatisch gesichert — er steht danach als
              eigener Eintrag in der Liste.
            </p>
          </div>
        </div>

        {!ergebnis && (
          <>
            <label className="mt-4 block">
              <span className="text-xs text-slate-600">
                Zur Bestaetigung den Namen der Instanz eingeben: <strong>{stackName}</strong>
              </span>
              <input
                autoFocus
                value={eingabe}
                onChange={(e) => setEingabe(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm"
              />
            </label>

            <div className="mt-4 flex items-center justify-end gap-2">
              <button onClick={onSchliessen} className="px-3 py-2 text-xs text-slate-600 hover:text-slate-800">
                Abbrechen
              </button>
              <button
                onClick={() => einspielen.mutate()}
                disabled={eingabe.trim() !== stackName || einspielen.isPending}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm text-white disabled:opacity-40"
              >
                {einspielen.isPending ? 'Spielt ein…' : 'Einspielen'}
              </button>
            </div>
          </>
        )}

        {ergebnis && (
          <>
            <p className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
              {ergebnis}
            </p>
            <div className="mt-4 flex justify-end">
              <button
                onClick={onSchliessen}
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white"
              >
                Schliessen
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function SicherungenPanel({
  instanceId,
  stackName,
}: {
  instanceId: string;
  stackName: string;
}) {
  const qc = useQueryClient();
  const [planOffen, setPlanOffen] = useState(false);
  const [meldung, setMeldung] = useState('');
  const [einspielDialog, setEinspielDialog] = useState<Sicherung | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['sicherungen', instanceId],
    queryFn: () => api.list(instanceId),
    // Eine laufende Sicherung soll sich von selbst aktualisieren.
    refetchInterval: (q) =>
      q.state.data?.sicherungen.some((s) => s.status === 'LAEUFT') ? 5_000 : false,
  });

  const jetzt = useMutation({
    mutationFn: () => api.jetzt(instanceId),
    onSuccess: (r) => {
      setMeldung(r.meldung);
      qc.invalidateQueries({ queryKey: ['sicherungen', instanceId] });
    },
    onError: (e: unknown) =>
      setMeldung(
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message
          ?? 'Die Sicherung ist fehlgeschlagen.',
      ),
  });

  const entfernen = useMutation({
    mutationFn: (id: string) => api.entfernen(instanceId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sicherungen', instanceId] }),
  });

  /**
   * Eine einzelne Sicherung aufheben.
   *
   * Der Plan rotiert weiter; diese eine bleibt. Gedacht fuer Staende, die man
   * behalten will — vor einem Umbau, nach einer Migration — ohne dafuer die
   * Aufbewahrung fuer alles hochzusetzen.
   */
  const schutz = useMutation({
    mutationFn: ({ id, geschuetzt }: { id: string; geschuetzt: boolean }) =>
      api.schutzSetzen(instanceId, id, geschuetzt),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sicherungen', instanceId] }),
  });

  if (isLoading) return <div className="h-40 animate-pulse rounded-xl bg-slate-100" />;
  if (!data) return null;

  const plan = data.plan;
  const hatDatenbank = data.datenbanken.length > 0;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-surface p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <Database size={16} className="mt-0.5 shrink-0 text-brand-600" />
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Datensicherung</h3>
              <p className="mt-0.5 text-xs text-slate-500">
                {hatDatenbank
                  ? `${data.datenbanken.map((d) => `${d.dienst} (${d.motor})`).join(', ')} — als Dump auf dem Wirt.`
                  : 'Diese Instanz hat keinen Datenbankcontainer.'}
              </p>
            </div>
          </div>
          {hatDatenbank && (
            <button
              onClick={() => { setMeldung(''); jetzt.mutate(); }}
              disabled={jetzt.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-xs font-medium text-white disabled:opacity-50"
            >
              {jetzt.isPending
                ? <><Loader2 size={12} className="animate-spin" /> sichert…</>
                : <><Play size={12} /> Jetzt sichern</>}
            </button>
          )}
        </div>

        {/* Die Grenze des Versprechens gehört sichtbar dorthin, wo jemand sich
            auf die Sicherung verlässt — nicht in eine Dokumentation daneben. */}
        {hatDatenbank && (
          <p className="mt-3 border-t border-slate-100 pt-2 text-[11px] leading-snug text-slate-400">
            Gesichert wird der Inhalt der Datenbank. Mediendateien und Uploads gehören
            nicht dazu — die liegen in Volumes und brauchen ein eigenes Verfahren.
          </p>
        )}

        {meldung && (
          <p className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-700">
            {meldung}
          </p>
        )}
      </div>

      {hatDatenbank && (
        <div className="rounded-xl border border-slate-200 bg-surface p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Clock size={15} className="text-slate-400" />
              <h3 className="text-sm font-semibold text-slate-800">Zeitplan</h3>
              {plan?.ebene === 'kunde' && (
                <span
                  className="rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700"
                  title="Dieser Plan gilt für alle Instanzen des Kunden. Wer hier speichert, legt einen eigenen für diese Instanz an."
                >
                  vom Kunden geerbt
                </span>
              )}
            </div>
            {!planOffen && (
              <button
                onClick={() => setPlanOffen(true)}
                className="text-xs font-medium text-brand-600 hover:text-brand-800"
              >
                {plan ? 'Ändern' : 'Zeitplan einrichten'}
              </button>
            )}
          </div>

          {!planOffen && (
            <p className="mt-2 text-xs text-slate-600">
              {!plan ? (
                <span className="text-slate-500">
                  Kein Zeitplan — es wird nur gesichert, wenn jemand den Knopf drückt.
                </span>
              ) : !plan.aktiv ? (
                <span className="text-slate-500">Eingerichtet, aber ausgeschaltet.</span>
              ) : (
                <>
                  Alle{' '}
                  <strong>
                    {zerlege(plan.intervallMinuten).zahl}{' '}
                    {EINHEITEN.find((e) => e.faktor === zerlege(plan.intervallMinuten).faktor)?.name}
                  </strong>
                  {'. '}
                  {plan.loeschschutz ? (
                    <span className="text-amber-700">Nichts wird automatisch geloescht.</span>
                  ) : (
                    <>
                      Aufgehoben werden <strong>{plan.behalteAnzahl}</strong>
                      {plan.loeschenNachTagen
                        ? <>, geloescht nach <strong>{plan.loeschenNachTagen} Tagen</strong></>
                        : ' (ohne Hoechstalter)'}
                      {plan.maxBelegungMb ? `, hoechstens ${plan.maxBelegungMb} MB` : ''}.
                    </>
                  )}
                  {plan.zuletztGelaufen && (
                    <span className="text-slate-400"> Zuletzt {zeitpunkt(plan.zuletztGelaufen)}.</span>
                  )}
                </>
              )}
            </p>
          )}

          {planOffen && (
            <PlanFormular
              plan={plan}
              instanceId={instanceId}
              onFertig={() => {
                setPlanOffen(false);
                qc.invalidateQueries({ queryKey: ['sicherungen', instanceId] });
              }}
            />
          )}
        </div>
      )}

      {einspielDialog && (
        <EinspielenDialog
          sicherung={einspielDialog}
          instanceId={instanceId}
          stackName={stackName}
          onSchliessen={() => setEinspielDialog(null)}
        />
      )}

      <div className="rounded-xl border border-slate-200 bg-surface p-5">
        <h3 className="text-sm font-semibold text-slate-800">
          Verlauf {data.sicherungen.length > 0 && (
            <span className="font-normal text-slate-400">({data.sicherungen.length})</span>
          )}
        </h3>

        {!data.sicherungen.length ? (
          <p className="mt-2 text-xs text-slate-500">Noch keine Sicherung.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-400">
                  <th className="pb-2 pr-3 font-medium">Zeitpunkt</th>
                  <th className="pb-2 pr-3 font-medium">Datenbank</th>
                  <th className="pb-2 pr-3 font-medium">Größe</th>
                  <th className="pb-2 pr-3 font-medium">Dauer</th>
                  <th className="pb-2 pr-3 font-medium">Auslöser</th>
                  <th className="pb-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.sicherungen.map((s) => (
                  <tr key={s.id} className="group">
                    <td className="py-2 pr-3 align-top">
                      <span className="flex items-center gap-1.5">
                        <StatusZeichen s={s} />
                        <span className="tabular-nums text-slate-700">{zeitpunkt(s.begonnenAm)}</span>
                      </span>
                      {s.fehler && (
                        <p className="ml-5 mt-0.5 text-[11px] leading-snug text-red-600">{s.fehler}</p>
                      )}
                    </td>
                    <td className="py-2 pr-3 align-top font-mono text-[11px] text-slate-600">
                      {s.dienst}
                    </td>
                    <td className="py-2 pr-3 align-top tabular-nums text-slate-600">
                      {s.status === 'WEGGERAEUMT' ? (
                        <span className="text-slate-400" title="Datei weggeräumt, Eintrag bleibt">
                          weggeräumt
                        </span>
                      ) : (
                        groesse(s.groesseBytes)
                      )}
                    </td>
                    <td className="py-2 pr-3 align-top tabular-nums text-slate-500">
                      {s.dauerSek === null ? '—' : `${s.dauerSek} s`}
                    </td>
                    <td className="py-2 pr-3 align-top text-slate-500">
                      {AUSLOESER_TEXT[s.ausloeser]}
                      {s.actor && <span className="block text-[11px] text-slate-400">{s.actor}</span>}
                    </td>
                    <td className="py-2 align-top text-right">
                      <span className="inline-flex items-center gap-2">
                        {/* Das Schloss steht immer sichtbar, nicht erst beim
                            Darueberfahren: es zeigt einen Zustand an, und ein
                            Zustand, den man erst suchen muss, ist keiner. */}
                        {s.status === 'OK' && (
                          <button
                            onClick={() => schutz.mutate({ id: s.id, geschuetzt: !s.geschuetzt })}
                            disabled={schutz.isPending}
                            title={s.geschuetzt
                              ? 'Geschützt — wird beim Aufräumen übersprungen. Klicken hebt den Schutz auf.'
                              : 'Vor dem automatischen Aufräumen schützen'}
                            className={s.geschuetzt
                              ? 'text-amber-600 hover:text-amber-700'
                              : 'text-slate-300 opacity-0 transition group-hover:opacity-100 hover:text-slate-600'}
                          >
                            {s.geschuetzt ? <Lock size={12} /> : <Unlock size={12} />}
                          </button>
                        )}
                        {s.status === 'OK' && s.pfad && (
                          <>
                            {/* Ein echter Link, kein Abruf im Code: der Browser
                                soll die Datei selbst entgegennehmen, statt sie
                                erst vollstaendig in den Speicher zu laden. */}
                            <a
                              href={api.downloadUrl(instanceId, s.id)}
                              title="Herunterladen"
                              className="text-slate-400 hover:text-brand-700"
                            >
                              <Download size={13} />
                            </a>
                            <button
                              onClick={() => setEinspielDialog(s)}
                              title="Diesen Stand einspielen"
                              className="text-slate-300 opacity-0 transition group-hover:opacity-100 hover:text-amber-600"
                            >
                              <Upload size={13} />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => {
                            const zusatz = s.geschuetzt
                              ? '\n\nDiese Sicherung ist geschützt — sie wurde bewusst aufgehoben.'
                              : '';
                            if (confirm(`Sicherung vom ${zeitpunkt(s.begonnenAm)} löschen?${zusatz}`)) {
                              entfernen.mutate(s.id);
                            }
                          }}
                          title="Sicherung löschen"
                          className="text-slate-300 opacity-0 transition group-hover:opacity-100 hover:text-red-600"
                        >
                          <Trash2 size={12} />
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
